import { Buffer, buffers, Channel, SagaIterator } from 'redux-saga';
import {
  actionChannel,
  all,
  call,
  cancel,
  cancelled,
  debounce,
  delay,
  flush,
  fork,
  join,
  put,
  race,
  select,
  spawn,
  take
} from 'redux-saga/effects';
import { Task } from '@redux-saga/types';
import Api from '../../utils/API';
import {
  accessRulesAction,
  addImagesToUploadSession,
  batchUpdateAfterUploadingOfImages,
  cancelDeleteImages,
  changeModalState,
  cleanUpImagesSuccess,
  clearImagesUploadSession,
  clearSelected,
  closeFullscreen,
  deleteImagesProgress,
  deleteImagesQueueComplete,
  duplicatesAction,
  enqueueDeleteImagesBatches,
  errorsGlobalError,
  fetchCollectionSuccess,
  fetchImages,
  fetchImagesSuccess,
  fetchPresetsAction,
  fetchSinglePortfolioAction,
  galleriesUploadingClearAction,
  galleryUploadingClearAction,
  galleryUploadingClearSuccess,
  compressSlotReleased,
  dismissNotification,
  imageUploadingNext,
  moveSelectedImagesToGallery,
  notify,
  pauseUploading,
  pushImagesToBatchUpdateQueue,
  resetImagesSelectionsFilter,
  removeImagesFromLocalCaches,
  restoreUploading,
  selectImage,
  selectImageSuccess,
  showNotifyFail,
  showNotifySuccess,
  trimImagesFromItems,
  startUploadingProcess,
  stopAllUpload,
  stopUploading,
  trackEventAction,
  updateGallery,
  updateImageUploadSessionData
} from '../../actions';
import ApiErrors from '../../utils/API/APIErrors';
import {
  getCollectionId,
  getSequentialUploads,
  getFullscreenState,
  getGalleriesList,
  getGalleryById,
  getGalleryImagesByGalleryID,
  getGalleryImagesDuplicateIDS,
  getImagesByGalleryID,
  getImagesList,
  getImagesToBatchUpdate,
  getSelectedImagesIDs,
  getSelectionByCollectionId,
  getUploadedImagesList,
  getUploadPauseState,
  getUploadSessionList,
  sortImages
} from '../../selectors';
import { getChosenUploadEndpoint } from '../../selectors/uploadEndpoint';
import { EndpointKey } from '../../types/uploadEndpoint';
import {
  IDeleteGalleryAction,
  IDeleteHeaderImageAction,
  IDragImageAction,
  IDragMultiImageAction,
  IGetSighUrlParams,
  IImageDimensions,
  IMoveImagesToGallery,
  ISagaAction,
  ISelectSelectionImageAction,
  ISetFocalPointParams,
  ISetGifMetaDataAction,
  IToggleImageWatermarkAction,
  IUploadImagesAction,
  CompressRetryEntry,
  ResultsMap,
  RunUploadLoopOptions,
  RunUploadLoopResult,
  RunRetryAttemptsArgs
} from '../../types';
import { IGallery, IImage } from '../../reducers';
import {
  chain,
  cloneDeep,
  difference,
  filter,
  find,
  findIndex,
  first,
  flatten,
  get,
  keyBy,
  maxBy,
  uniq,
  uniqBy,
  uniqueId
} from 'lodash';
import { handleOfflineResponse, multipleAttempts } from '../global';
import { uploadImageToS3 } from '../collections';
import moment from 'moment';
import {
  arrayMoveHelper,
  createOnlineChannel,
  getDuplicateId,
  getOrderedDnDImages,
  getTranslationKey,
  isImageSizeAccepted,
  isNavigatorOnline,
  processImageDimensions,
  waitForOnline
} from '../../utils';
import { DEFAULT_IMAGE, ImageUploadAnalyticsEvent } from '../../constants';
/* tslint:disable */
import { setModalOpenState } from '../../../actions';

// Upload concurrency / polling tunables.
const MAX_CONCURRENT_UPLOADS = 3;
const MAX_CONCURRENT_REPLACEMENTS = 3;
// Compress runs after sign+S3 releases its slot, so the post-S3 phase
// (compress / gif) needs its own cap — otherwise it can pile up unboundedly
// on slow ImageProcessing requests.
const COMPRESS_MAX_CONCURRENT = 3;
// BE-side compression of large originals can outlive the upload itself by
// minutes: 5s → 15s backoff over 24 attempts ≈ 5.5 min of tracking.
const MAX_COMPRESSION_POLL_ATTEMPTS = 24;
const COMPRESSION_POLL_INTERVAL_MS = 5000;
const COMPRESSION_POLL_MAX_INTERVAL_MS = 15_000;
const COMPRESSION_POLL_FETCH_TIMEOUT_MS = 10_000;
// Fresh lists in a row without the session entry's BE record before we stop
// tracking it (deleted while compressing).
const COMPRESSION_POLL_MISSING_LIMIT = 3;
// Per-session retry cap; high enough to survive flaky-network blips where
// the inner offline check skips items mid-attempt.
const MAX_RETRY_ATTEMPTS = 10;
// Session-wide backpressure timestamp. When BE returns 503, every fork waits
// out the cooldown before its next external request — prevents the rest of
// the burst from hammering a struggling/restarting dyno.
const COOLDOWN_BASE_MS = 3000;
const COOLDOWN_JITTER_MS = 2000;

// Session state shared across batches in the same drain window, read by
// reconcileUploadSessionSaga once the channel empties. See ResultsMap in
// modules/types/upload for the two-state encoding.
let sessionPayloads: IImage[] = [];
// Two-state encoding:
//   key missing          → needs full re-upload (never reached S3 PUT)
//   value === null       → fully done (S3 + compress/gif both OK)
//   value: CompressRetryEntry → S3 ok, compress/gif still needs retry
const sessionResults: ResultsMap = new Map();

// Endpoint circuit breaker: confirmed broken once a single image fails this
// many PUT attempts. Then queued images fail fast, retry rounds stop and the
// endpoint notification shows. A successful PUT clears that image's count.
const MAX_PROVIDER_FAILURES_PER_IMAGE = 5;
const providerFailuresPerImage = new Map<string, number>();
let endpointFailureConfirmed = false;
const isProviderCircuitOpen = () => endpointFailureConfirmed;

const trackProviderFailure = (imageId: string) => {
  const failures = (providerFailuresPerImage.get(imageId) || 0) + 1;
  providerFailuresPerImage.set(imageId, failures);

  if (failures >= MAX_PROVIDER_FAILURES_PER_IMAGE) {
    endpointFailureConfirmed = true;
  }
};

// A PUT still in flight after this long triggers a once-per-session
// slow-connection warning; the upload itself keeps running.
const SLOW_UPLOAD_WARNING_MS = 5 * 60_000;
let slowUploadWarningShown = false;

const resetUploadSession = () => {
  sessionPayloads = [];
  sessionResults.clear();
  providerFailuresPerImage.clear();
  endpointFailureConfirmed = false;
  slowUploadWarningShown = false;
};

// Compress phase has its own semaphore. The sign+S3 phase already releases
// its slot via `imageUploadingNext` (so the next fork can start) — without
// a second cap, compress requests can pile up unboundedly (observed up to
// 20+ in HAR captures). Lazy-init a shared actionChannel so every fork
// competes fairly for the next release.
let inflightCompress = 0;
let compressSlotChannel: Channel<any> | null = null;

function* acquireCompressSlot(): SagaIterator {
  if (!compressSlotChannel) {
    compressSlotChannel = (yield actionChannel(
      compressSlotReleased,
      buffers.expanding()
    )) as Channel<any>;
  }
  while (inflightCompress >= COMPRESS_MAX_CONCURRENT) {
    yield take(compressSlotChannel);
  }
  inflightCompress++;
}

function* releaseCompressSlot(): SagaIterator {
  inflightCompress--;
  yield put(compressSlotReleased());
}

// 503 backpressure. Set as soon as `multipleAttempts` catches a 503 (on the
// first attempt, not after the in-band 25s retry budget). Read by
// `respectUploadCooldown` before every external upload request — gives the
// dyno 3-5s of breathing room before the next burst.
let uploadCooldownUntil = 0;

export function markUploadServerBackpressure(): void {
  uploadCooldownUntil = Math.max(
    uploadCooldownUntil,
    Date.now() + COOLDOWN_BASE_MS + Math.random() * COOLDOWN_JITTER_MS
  );
}

function* respectUploadCooldown(): SagaIterator {
  const wait = uploadCooldownUntil - Date.now();
  if (wait > 0) yield delay(wait);
}

interface IPendingDeleteRequest {
  collectionID: string;
  imageIDs: string[];
  originalImageNames: string[];
  affectedGalleriesIds: string[];
}

// Drained by checkAndDrainOfflineDeleteQueueSaga on `online`
const offlineDeleteQueue: IPendingDeleteRequest[] = [];

// Set when an upload session ends with failures while offline; drained by
// checkAndDrainOfflineDeleteQueueSaga once the connection returns (SBK-6252)
let pendingIncompleteCleanup = false;

export const fetchImagesSaga = function* (): SagaIterator {
  try {
    let collectionID = yield select(getCollectionId);

    if (!Boolean(collectionID.length)) {
      yield take(fetchCollectionSuccess);
    }
    collectionID = yield select(getCollectionId);
    const response = yield call(Api.Images.get, {
      collection_id: collectionID,
      ownerRequest: true
    });
    ApiErrors.checkOnApiError(response);

    const appIcon = find(response, { isIcon: true }) || {};
    const items = filter(response, (image) => !image.isIcon && image.status !== 'deleting');
    yield put(fetchImagesSuccess({ items, appIcon }));
  } catch (e) {
    // @ts-ignore
    yield put(errorsGlobalError(e));
  }
};

export const setImageTitleSaga = function* ({ payload }: ISagaAction<IImage>): SagaIterator {
  try {
    const { _id, gallery_id } = payload;
    const images = yield select(getImagesList);

    const response = yield call(Api.Images.setImageTitle, {
      gallery_id,
      image_id: _id,
      isForSmallScreen: false
    });
    ApiErrors.checkOnApiError(response);

    const updated = images.map((image: IImage) =>
      image.gallery_id === gallery_id
        ? {
            ...image,
            isTitleImage: image._id === _id,
            focalPoints: {
              ...image.focalPoints,
              desktop: null
            }
          }
        : image
    );

    yield put(fetchImagesSuccess({ items: updated }));
    yield put(showNotifySuccess({}));
  } catch (e) {
    // @ts-ignore
    yield put(errorsGlobalError(e));
  }
};

export const setFocalPointSaga = function* ({
  payload
}: ISagaAction<ISetFocalPointParams>): SagaIterator {
  try {
    const { portfolioId, image_id, focalPoint, screenType } = payload;
    const response = yield call(Api.Images.setFocalPoint, payload);
    ApiErrors.checkOnApiError(response);
    if (portfolioId) {
      yield put(fetchSinglePortfolioAction({ portfolioId }));
    }

    const images = yield select(getImagesList);
    const updated = images.map((image: IImage) =>
      image._id === image_id
        ? {
            ...image,
            focalPoints: {
              ...image.focalPoints,
              [screenType]: focalPoint
            }
          }
        : image
    );

    yield put(fetchImagesSuccess({ items: updated }));
    yield put(showNotifySuccess({}));
  } catch (e) {
    // @ts-ignore
    yield put(errorsGlobalError(e));
  }
};

export const setSmallScreenImageTitleSaga = function* ({
  payload
}: ISagaAction<IImage>): SagaIterator {
  try {
    const { _id, gallery_id } = payload;
    const images = yield select(getImagesList);

    const response = yield call(Api.Images.setImageTitle, {
      gallery_id,
      image_id: _id,
      isForSmallScreen: true
    });
    ApiErrors.checkOnApiError(response);

    const updated = images.map((image: IImage) =>
      image.gallery_id === gallery_id
        ? {
            ...image,
            isSmallScreenTitleImage: image._id === _id,
            focalPoints: {
              ...image.focalPoints,
              mobile: null
            }
          }
        : image
    );

    yield put(fetchImagesSuccess({ items: updated }));
    yield put(showNotifySuccess({}));
  } catch (e) {
    // @ts-ignore
    yield put(errorsGlobalError(e));
  }
};

export const deleteImageSaga = function* ({ payload }: ISagaAction<string>): SagaIterator {
  try {
    const response = yield call(Api.Images.delete, {
      image_id: payload
    });
    ApiErrors.checkOnApiError(response);

    const fullscreenState = yield select(getFullscreenState);
    let images: IImage[] = yield select(getImagesList);
    const deletedImageIndex = images.findIndex((image: IImage) => image._id === payload);
    const deletedImage = images[deletedImageIndex];
    images = images.filter((image: IImage) => image._id !== payload);

    if (deletedImage) {
      yield put(
        removeImagesFromLocalCaches({
          imageIds: [payload],
          originalImageNames: deletedImage.originalImageName ? [deletedImage.originalImageName] : []
        })
      );
    } else {
      yield put(removeImagesFromLocalCaches({ imageIds: [payload] }));
    }

    if (!deletedImage) {
      yield put(fetchImagesSuccess({ items: images }));
      yield put(showNotifySuccess({}));

      return;
    }

    const gallery = yield select((state) => getGalleryById(state)(deletedImage.gallery_id));

    if (gallery.sortBy === 'manualOrder') {
      images = fixGalleryManualOrder(images, gallery);
    }

    if (!images.length && fullscreenState.isOpen) {
      yield put(closeFullscreen());
    }

    yield put(fetchImagesSuccess({ items: images }));
    yield put(showNotifySuccess({}));
  } catch (e) {
    // @ts-ignore
    yield put(errorsGlobalError(e));
  }
};

export const deleteImagesSaga = function* (): SagaIterator {
  const images: IImage[] = yield select(getImagesList);
  const selected: string[] = yield select(getSelectedImagesIDs);
  const collectionID = yield select(getCollectionId);

  if (!selected.length) return;

  const selectedSet = new Set(selected);
  const selectedImages = images.filter((img) => selectedSet.has(img._id));

  // Expand each selected image with its _shadowIds — server-side duplicates
  // (older replaced/orphan twins) that dedupeImages collapsed in the reducer.
  // Without this, those orphan docs survive every bulk delete (the UI never
  // sees them, so they're never selected) and accumulate forever.
  // Keep imageIDs / originalImageNames / affectedGalleriesIds index-parallel
  // because processDeleteImagesQueueSaga slices them by the same offsets.
  const imagesById = new Map(selectedImages.map((img) => [img._id, img]));
  const expandedIDs: string[] = [];
  const originalImageNames: string[] = [];
  const affectedGalleriesIds: string[] = [];
  const seen = new Set<string>();

  const pushId = (id: string, img: IImage | undefined) => {
    if (!id || seen.has(id)) return;
    seen.add(id);
    expandedIDs.push(id);
    originalImageNames.push((img && img.originalImageName) || '');
    affectedGalleriesIds.push((img && img.gallery_id) || '');
  };

  selected.forEach((id) => {
    const img = imagesById.get(id);
    pushId(id, img);
    (img?._shadowIds || []).forEach((shadowId) => pushId(shadowId, img));
  });

  yield put(
    enqueueDeleteImagesBatches({
      collectionID,
      imageIDs: expandedIDs,
      originalImageNames,
      affectedGalleriesIds
    })
  );
};

const DELETE_BATCH_SIZE = 500;
const DELETE_MAX_ATTEMPTS = 5;
// Heroku doesn't pin requests — all N may land on the same web dyno. 4+
// parallel batches reproduced the OOM that crashed the dyno on 2026-05-04
// because of the manualOrder-recalculation `findOne` + `find` after each
// delete. Don't raise without re-validating peak heap.
const DELETE_CONCURRENCY = 3;

// HTTPClient unwraps 2xx to `response.data` (see modules/utils/HTTPSClient),
// so a successful empty DELETE body lands as `''` with no `.code` field.
// Errors come back as `{ code, ... }` where `code === 0` is a network error
// and 4xx/5xx mirror HTTP status. Returns 'offline' to signal the caller to
// park and resume on `online`.
const sendDeleteBatchSaga = function* (
  batch: string[],
  collectionID: string
): SagaIterator<'offline' | void> {
  let attempt = 0;
  while (true) {
    const response = yield call(Api.Images.deleteByIDs, {
      collection_id: collectionID,
      image_ids: batch
    });

    const code: number | undefined = response?.code;

    if (typeof code !== 'number') return;
    if (code === 0 && !isNavigatorOnline()) return 'offline';
    if (code >= 400 && code < 500) {
      ApiErrors.checkOnApiError(response);

      return;
    }

    attempt += 1;
    if (attempt >= DELETE_MAX_ATTEMPTS) {
      // navigator.onLine can lag real connectivity by several seconds, so
      // 5 consecutive `code: 0` is offline regardless of what the flag says —
      // park and wait for `online` rather than fail the queue.
      if (code === 0) return 'offline';
      ApiErrors.checkOnApiError(response);

      return;
    }
    yield delay(5000 * attempt);
  }
};

export const processDeleteImagesQueueSaga = function* (action: {
  payload: {
    collectionID: string;
    imageIDs: string[];
    originalImageNames: string[];
    affectedGalleriesIds: string[];
  };
}): SagaIterator {
  const { collectionID, imageIDs, originalImageNames, affectedGalleriesIds } = action.payload;
  const total = imageIDs.length;

  const batchCount = Math.ceil(imageIDs.length / DELETE_BATCH_SIZE);
  const sliceFor = (idx: number) => {
    const start = idx * DELETE_BATCH_SIZE;

    return {
      ids: imageIDs.slice(start, start + DELETE_BATCH_SIZE),
      names: originalImageNames.slice(start, start + DELETE_BATCH_SIZE),
      start
    };
  };

  const poolState: {
    nextIndex: number;
    completed: Set<number>;
    error: unknown;
    offlineHit: boolean;
  } = { nextIndex: 0, completed: new Set(), error: null, offlineHit: false };

  const deleteWorker = function* (): SagaIterator {
    while (true) {
      if (poolState.error || poolState.offlineHit) return;
      const myIndex = poolState.nextIndex;
      poolState.nextIndex += 1;
      if (myIndex >= batchCount) return;

      const { ids, names } = sliceFor(myIndex);

      try {
        const result = yield call(sendDeleteBatchSaga, ids, collectionID);
        if (result === 'offline') {
          poolState.offlineHit = true;

          return;
        }
        poolState.completed.add(myIndex);
        // Per-batch trim is what keeps the UI honest if the user goes offline
        // mid-run: ids deleted before parking would otherwise stay in
        // state.items and the post-drain cleanup wouldn't remove them.
        // removeImagesFromLocalCaches doesn't touch state.items, so
        // trimImagesFromItems handles it without resetting the spinner flag.
        yield put(removeImagesFromLocalCaches({ imageIds: ids, originalImageNames: names }));
        yield put(trimImagesFromItems({ imageIds: ids }));
        yield put(
          deleteImagesProgress({ done: poolState.completed.size * DELETE_BATCH_SIZE, total })
        );
      } catch (e) {
        if (poolState.error === null) poolState.error = e;

        return;
      }
    }
  };

  try {
    const workerCount = Math.min(DELETE_CONCURRENCY, batchCount);
    const tasks = [];
    for (let i = 0; i < workerCount; i += 1) {
      tasks.push(yield fork(deleteWorker));
    }
    yield all(tasks.map((task) => join(task)));

    if (poolState.offlineHit) {
      // Missing indices aren't contiguous under concurrency — the failed
      // batch can sit between completed ones — so collect every unfinished
      // index rather than slicing from the first miss.
      const pendingIDs: string[] = [];
      const pendingNames: string[] = [];
      for (let idx = 0; idx < batchCount; idx += 1) {
        if (poolState.completed.has(idx)) continue;
        const { ids, names } = sliceFor(idx);
        pendingIDs.push(...ids);
        pendingNames.push(...names);
      }
      offlineDeleteQueue.push({
        collectionID,
        imageIDs: pendingIDs,
        originalImageNames: pendingNames,
        affectedGalleriesIds
      });
      yield put(showNotifyFail({ message: getTranslationKey('offlineMessage') }));

      return;
    }

    if (poolState.error) throw poolState.error;

    const galleries: IGallery[] = yield select(getGalleriesList);
    const images: IImage[] = yield select(getImagesList);
    const idSet = new Set(imageIDs);
    const galleriesToFix = galleries.filter(
      (gallery) => affectedGalleriesIds.includes(gallery._id) && gallery.sortBy === 'manualOrder'
    );
    const updated = images.filter((image: IImage) => !idSet.has(image._id));
    const imagesWithCorrectManualOrder = fixGalleriesManualOrder(updated, galleriesToFix);

    yield put(removeImagesFromLocalCaches({ imageIds: imageIDs, originalImageNames }));
    yield put(fetchImagesSuccess({ items: imagesWithCorrectManualOrder }));
    yield put(clearSelected());
    yield put(showNotifySuccess({}));
  } catch (e) {
    // Re-sync so a partially-applied delete doesn't desync the gallery count.
    yield put(fetchImages());
    // @ts-ignore
    yield put(errorsGlobalError(e));
  } finally {
    yield put(deleteImagesQueueComplete());
    // refresh the displayed storage counter after the backend decrement
    yield put(accessRulesAction());
  }
};

export function* checkAndDrainOfflineDeleteQueueSaga(): SagaIterator {
  const onlineChannel = createOnlineChannel();

  try {
    while (true) {
      yield race({ tick: delay(10_000), online: take(onlineChannel) });

      if (!isNavigatorOnline()) continue;

      // finish the cleanup an offline-interrupted session left behind; wait
      // while a new session is running so in-flight uploads aren't touched
      if (pendingIncompleteCleanup && sessionPayloads.length === 0) {
        pendingIncompleteCleanup = false;
        yield call(softDeleteIncompleteUploadsSaga);
        yield put(accessRulesAction());
      }

      if (!offlineDeleteQueue.length) continue;

      const pending = offlineDeleteQueue.splice(0);
      for (let i = 0; i < pending.length; i += 1) {
        if (!isNavigatorOnline()) {
          offlineDeleteQueue.push(...pending.slice(i));
          break;
        }
        yield put(enqueueDeleteImagesBatches(pending[i]));
      }
    }
  } finally {
    if (yield cancelled()) {
      // intentionally empty
    }
  }
}

// Long-running watcher analogous to watchRequest for uploads: serialises every
// enqueued delete through an expanding-buffer actionChannel so concurrent UI
// triggers don't pile up parallel HTTP work, and gives us a single race point
// for cancellation.
export function* watchDeleteImagesRequest(): SagaIterator {
  const buffer: Buffer<any> = buffers.expanding();
  const requestChannel = yield actionChannel(enqueueDeleteImagesBatches, buffer);

  try {
    while (true) {
      const action = yield take(requestChannel);
      yield race({
        task: call(processDeleteImagesQueueSaga, action),
        cancel: take(cancelDeleteImages)
      });
    }
  } finally {
    if (yield cancelled()) {
      // channel torn down with the saga middleware
    }
  }
}

function fixGalleryManualOrder(allImages: IImage[], gallery: IGallery): IImage[] {
  const images = cloneDeep(allImages);
  const galleryImages = images.filter((image) => image.gallery_id === gallery._id);
  const imagesWithCorrectManualOrder = sortImages(
    galleryImages,
    gallery.sortBy,
    gallery.sortOrder
  ).map((image, idx) => ({ ...image, manualOrder: idx }));

  images.forEach((image) => {
    const imageToUpdate = imagesWithCorrectManualOrder.find((img) => img._id === image._id);
    if (imageToUpdate) {
      image.manualOrder = imageToUpdate.manualOrder;
    }
  });

  return images;
}

function fixGalleriesManualOrder(allImages: IImage[], galleries: IGallery[]): IImage[] {
  let images = cloneDeep(allImages);
  galleries.forEach((gallery) => {
    images = fixGalleryManualOrder(images, gallery);
  });

  return images;
}

export const deleteGalleryImagesSaga = function* ({
  payload
}: ISagaAction<IDeleteGalleryAction>): SagaIterator {
  try {
    const { gallery_id } = payload;
    const images = yield select(getImagesList);
    const collectionID = yield select(getCollectionId);

    const response = yield call(Api.Images.deleteByIDs, {
      collection_id: collectionID,
      gallery_id
    });

    ApiErrors.checkOnApiError(response);
    const updated = images.filter((image: IImage) => image.gallery_id !== gallery_id);
    yield put(fetchImagesSuccess({ items: updated }));
    yield put(showNotifySuccess({}));
    yield put(stopUploading(gallery_id));
  } catch (e) {
    // @ts-ignore
    yield put(errorsGlobalError(e));
  }
};

export const selectImageSaga = function* ({ payload }: ISagaAction<string>): SagaIterator {
  const selected = yield select(getSelectedImagesIDs);

  let IDs = selected;
  if (selected.includes(payload)) {
    IDs = selected.filter((id: string) => id !== payload);
  } else {
    IDs.push(payload);
  }
  yield put(resetImagesSelectionsFilter(true));
  yield put(selectImageSuccess([...IDs]));
};

export const selectImagesSaga = function* ({ payload }: ISagaAction<string[]>): SagaIterator {
  yield put(resetImagesSelectionsFilter(true));
  yield put(selectImageSuccess([...payload]));
};

export const invertSelectedImagesSaga = function* (): SagaIterator {
  const selected = yield select(getSelectedImagesIDs);
  const allImages = yield select(getUploadedImagesList);
  const allImagesIDs = allImages.map(({ _id }: IImage) => _id);
  yield put(selectImageSuccess(difference(allImagesIDs, selected)));
};

export const moveSelectedImagesToGallerySaga = function* ({
  payload
}: ISagaAction<string>): SagaIterator {
  const selectedImages = yield select(getSelectedImagesIDs);
  yield call(moveImagesToGallerySaga, {
    galleryID: payload,
    imagesIDs: selectedImages
  });
  yield put(clearSelected());
};

export const moveImagesToGallerySaga = function* ({
  galleryID,
  imagesIDs,
  isSilent
}: IMoveImagesToGallery): SagaIterator {
  try {
    const images = yield select(getImagesList);
    const collectionID = yield select(getCollectionId);

    const response = yield call(Api.Images.moveTo, {
      collection_id: collectionID,
      target_gallery_id: galleryID,
      image_ids: imagesIDs,
      addToTheEnd: true
    });

    ApiErrors.checkOnApiError(response);
    const updated = uniqBy(response.concat(images), '_id') as unknown as IImage[];

    const galleries: IGallery[] = yield select(getGalleriesList);
    const galleriesToFix = galleries.filter((gallery) => gallery.sortBy === 'manualOrder');
    const imagesWithCorrectManualOrder = fixGalleriesManualOrder(updated, galleriesToFix);

    yield put(fetchImagesSuccess({ items: imagesWithCorrectManualOrder }));
    if (!isSilent) {
      yield put(showNotifySuccess({}));
    }
  } catch (e) {
    // @ts-ignore
    yield put(errorsGlobalError(e));
  }
};

export const actionDropImageSaga = function* ({
  payload
}: ISagaAction<IDragImageAction>): SagaIterator {
  try {
    const { drop_id, drag_id, gallery_id, images } = payload;

    const galleryImages = images;

    const allImage = yield select(getImagesList);
    const dragImage = find(allImage, { _id: drag_id });
    const dropImage = find(allImage, { _id: drop_id });
    const collectionID = yield select(getCollectionId);
    const gallery = yield select((state) => getGalleryById(state)(gallery_id));
    const isAnotherGallery = dragImage.gallery_id !== gallery_id;
    if (isAnotherGallery) {
      yield put(selectImage(drag_id));
      yield put(moveSelectedImagesToGallery(gallery_id));
      yield take(clearSelected);
      dragImage.gallery_id = gallery_id;
      dragImage._watermark = gallery._watermark;
    }

    const defaultLength = galleryImages.length > 0 ? galleryImages.length : 0;
    const dragIndex = findIndex(galleryImages, (image: IImage) => image._id === drag_id);
    let targetIndex = dropImage
      ? findIndex(galleryImages, (image: IImage) => image._id === dropImage._id)
      : defaultLength;

    if (dragIndex < targetIndex && !isAnotherGallery) {
      targetIndex -= 1;
    }

    if (isAnotherGallery) {
      galleryImages.splice(targetIndex, 0, dragImage);
    }

    const movedImages = yield select(getImagesList);
    let updated = !isAnotherGallery
      ? arrayMoveHelper(galleryImages, dragIndex, targetIndex)
      : galleryImages;
    updated = updated.map((image: IImage, index: number) => ({
      ...image,
      manualOrder: index
    }));

    yield put(fetchImagesSuccess({ items: uniqBy(updated.concat(movedImages), '_id') }));

    const response = yield call(Api.Images.order, {
      collection_id: collectionID,
      gallery_id,
      image_id: drag_id,
      order: targetIndex
    });
    ApiErrors.checkOnApiError(response);

    if (gallery.sortBy !== 'manualOrder') {
      yield put(updateGallery({ gallery_id, data: { sortBy: 'manualOrder' }, isSilent: true }));
    }

    yield put(showNotifySuccess({}));
  } catch (e) {
    // @ts-ignore
    yield put(errorsGlobalError(e));
  }
};

// Re-shuffles a gallery: the BE assigns a fresh randomOrder permutation, then we
// persist sortBy=randomOrder and refetch so the new order is reflected. A new
// shuffle on every call backs "different every time". SBK-9036.
export const randomizeImagesOrderSaga = function* ({
  payload
}: ISagaAction<{ gallery_id: string }>): SagaIterator {
  try {
    const { gallery_id } = payload;
    const collectionID = yield select(getCollectionId);
    const gallery = yield select((state) => getGalleryById(state)(gallery_id));

    const response = yield call(Api.Images.randomizeOrder, {
      collection_id: collectionID,
      gallery_id
    });
    ApiErrors.checkOnApiError(response);

    if (gallery.sortBy !== 'randomOrder') {
      yield put(updateGallery({ gallery_id, data: { sortBy: 'randomOrder' }, isSilent: true }));
    }

    yield call(fetchImagesSaga);
    yield put(showNotifySuccess({}));
  } catch (e) {
    // @ts-ignore
    yield put(errorsGlobalError(e));
  }
};

export const actionDropMultiImageSaga = function* ({
  payload
}: ISagaAction<IDragMultiImageAction>): SagaIterator {
  try {
    const { drop_id, gallery_id, images, isMoveOnly } = payload;

    const collectionID = yield select(getCollectionId);
    const selectedIDs = yield select(getSelectedImagesIDs);
    const allImage = yield select(getImagesList);
    const galleries = yield select(getGalleriesList);
    const dropImage = find(allImage, { _id: drop_id });
    const gallery = yield select((state) => getGalleryById(state)(gallery_id));
    const {
      imagesToMove,
      images: orderedDropImages,
      targetImagesIDS
    } = getOrderedDnDImages(selectedIDs, allImage, galleries, gallery_id);
    const galleryImages = images.filter((image) => !targetImagesIDS.includes(image._id));
    const allGalleriesImages = images.filter((image) => image.gallery_id === gallery_id);

    if (imagesToMove.length) {
      yield call(moveImagesToGallerySaga, {
        galleryID: gallery_id,
        imagesIDs: imagesToMove,
        isSilent: true
      });
    }

    if (isMoveOnly) {
      yield put(showNotifySuccess({}));

      return;
    }

    const defaultLength = galleryImages.length > 0 ? galleryImages.length : 0;
    const firstDragIndex = allGalleriesImages.findIndex(
      (image) => image._id === targetImagesIDS[0]
    );

    let targetIndex = dropImage
      ? findIndex(galleryImages, (image: IImage) => image._id === dropImage._id)
      : defaultLength;

    if (firstDragIndex <= targetIndex) {
      targetIndex += 1;
    }

    // @ts-ignore
    galleryImages.splice(targetIndex, 0, orderedDropImages);

    const movedImages = yield select(getImagesList);

    const updated = flatten(galleryImages).map((image: IImage, index: number) => ({
      ...image,
      manualOrder: index,
      gallery_id,
      _watermark: gallery._watermark
    }));

    yield put(fetchImagesSuccess({ items: uniqBy(updated.concat(movedImages), '_id') }));

    const response = yield call(Api.Images.multipleOrder, {
      collection_id: collectionID,
      gallery_id,
      images: updated.map((image) => ({
        image_id: image._id,
        order: image.manualOrder
      }))
    });
    ApiErrors.checkOnApiError(response);

    if (gallery.sortBy !== 'manualOrder') {
      yield put(updateGallery({ gallery_id, data: { sortBy: 'manualOrder' }, isSilent: true }));
    }

    yield put(showNotifySuccess({}));
  } catch (e) {
    // @ts-ignore
    yield put(errorsGlobalError(e));
  }
};

export const toggleImageWatermarkSaga = function* ({
  payload
}: ISagaAction<IToggleImageWatermarkAction>): SagaIterator {
  try {
    const collectionID = yield select(getCollectionId);
    const response = yield call(Api.Images.toggleWatermark, {
      collection_id: collectionID,
      ...payload
    });
    ApiErrors.checkOnApiError(response);

    const images = yield select(getImagesList);
    const updatedImages = images.map((image: IImage) =>
      response._id === image._id ? response : image
    );
    yield put(fetchImagesSuccess({ items: updatedImages }));
    yield put(showNotifySuccess({}));
  } catch (e) {
    // @ts-ignore
    yield put(errorsGlobalError(e));
  }
};

export function* watchBatchImagesUpdateSaga(): SagaIterator {
  // `pushImagesToBatchUpdateQueue` may happen asynchronously (e.g. after S3 when compression finishes).
  // We only want to do a *final* flush when uploading is idle; otherwise we break the "update in chunks of 30"
  // behavior and the UI starts re-rendering after each image.
  yield debounce(500, pushImagesToBatchUpdateQueue, function* (): SagaIterator {
    const uploadSessionImages = yield select(getUploadSessionList);
    const isUploadingInProgress = Object.values(
      (uploadSessionImages || {}) as Record<string, IImage[]>
    ).some((albumImages) =>
      (albumImages || []).some((img: IImage) => img?.uploadingStatus === 'uploading')
    );

    if (isUploadingInProgress) return;

    const imagesToUpdate = yield select(getImagesToBatchUpdate);
    if (imagesToUpdate?.length) {
      yield put(batchUpdateAfterUploadingOfImages());
    }
  });
}

function* refetchImagesSaga(times: number = 1, delayTime: number = 1000) {
  for (let i = 0; i <= times; i++) {
    yield delay(delayTime);
    yield put(fetchImages());
  }
}

const openUploadSettingsButton = () => ({
  name: getTranslationKey('progressUploadModal.openUploadSettings'),
  primary: true,
  onClick: () => {
    window.open(`${window.location.pathname}#/settings?tab=upload-endpoint`, '_blank', 'noopener');
  }
});

// Sticky endpoint-failure toast; fires only once the breaker confirmed the
// endpoint problem. Fixed id prevents duplicate toasts across flows.
function* notifyUploadFailureSaga(failedCount: number): SagaIterator {
  if (!failedCount || !endpointFailureConfirmed || !isNavigatorOnline()) return;

  // The hard failure supersedes the soft slow-connection warning.
  yield put(dismissNotification('upload-slow-connection'));

  yield put(
    notify({
      id: 'upload-endpoint-failure',
      title: getTranslationKey('error') as string,
      message: getTranslationKey('progressUploadModal.uploadFailedEndpointHint', {
        failedCount
      }),
      status: 'error',
      position: 'top-center',
      dismissible: true,
      dismissAfter: 0,
      buttons: [openUploadSettingsButton()]
    })
  );
}

// Slow-connection warning, at most once per upload session.
function* notifySlowUploadSaga(): SagaIterator {
  if (slowUploadWarningShown || !isNavigatorOnline()) return;
  slowUploadWarningShown = true;

  // Track how many users hit the slow-upload warning
  if (window?.newrelic?.noticeError) {
    window.newrelic.noticeError('SlowUploadWarningShown', {
      thresholdMs: SLOW_UPLOAD_WARNING_MS
    });
  }

  yield put(
    notify({
      id: 'upload-slow-connection',
      title: getTranslationKey('progressUploadModal.slowUploadWarningTitle') as string,
      message: getTranslationKey('progressUploadModal.slowUploadWarning'),
      status: 'warning',
      position: 'top-center',
      dismissible: true,
      dismissAfter: 0,
      buttons: [openUploadSettingsButton()]
    })
  );
}

let newDuplicateImagesQueu: IImage[] = [];

function* checkAndUploadDuplicateImagesSaga(buffer: Buffer<any>): SagaIterator {
  if (!buffer.isEmpty() || !newDuplicateImagesQueu.length) return;

  // Snapshot + dedupe the queue into a stable payload list keyed by
  // gallery_id + originalImageName.
  const replacementPayloads: IImage[] = [];
  const seenKeys = new Set<string>();
  for (const img of newDuplicateImagesQueu) {
    const key = `${img.gallery_id}__${img.originalImageName}`;
    if (seenKeys.has(key)) continue;
    seenKeys.add(key);
    replacementPayloads.push(img);
  }
  newDuplicateImagesQueu = [];

  const replacementResults: ResultsMap = new Map();
  const affectedAlbumIds: string[] = [];
  const allReplacementTasks: Task[] = [];

  // Keep the duplicateId the user chose in the modal. We only re-resolve when
  // the target is itself a pending upload (pseudo `uploading_image_*` id) — the
  // server doesn't know that id yet, so we look up the real backend id by name
  // from the current gallery state.
  const resolvedReplacementPayloads: IImage[] = [];
  for (const image of replacementPayloads) {
    let resolvedDuplicateId: string = image.duplicateId || '';
    const isPseudoId = resolvedDuplicateId.startsWith('uploading_image_');

    if (isPseudoId) {
      const galleryImages: IImage[] = yield select((state) =>
        getGalleryImagesByGalleryID(state)(image.gallery_id)
      );
      // prefer the uploaded twin — the list may also contain a failed-upload
      // doc with the same name (SBK-6252)
      const sameNameImages = galleryImages.filter(
        (uplodedImage: IImage) =>
          uplodedImage.gallery_id === image.gallery_id &&
          uplodedImage.originalImageName === image.originalImageName
      );
      const duplicateImage =
        sameNameImages.find((img: IImage) => img.status === 'uploaded') || sameNameImages[0];
      resolvedDuplicateId = duplicateImage?._id || '';
    }

    resolvedReplacementPayloads.push(
      resolvedDuplicateId ? { ...image, duplicateId: resolvedDuplicateId } : image
    );
  }

  // Phase 1 — full re-upload for replacements that never reached S3 PUT.
  yield call(runRetryAttempts, {
    payloads: resolvedReplacementPayloads,
    resultsMap: replacementResults,
    logLabel: 'replacement',
    *processBatch(missing) {
      const sequential = yield select(getSequentialUploads);
      const { tasks } = yield call(runUploadLoop, missing, {
        startTime: performance.now(),
        timestampAlbumId: get(first(missing), 'gallery_id'),
        skipOnError: false,
        maxConcurrency: sequential ? 1 : MAX_CONCURRENT_REPLACEMENTS,
        resultsMap: replacementResults,
        checkSession: false,
        routeDuplicates: false,
        logLabel: 'replacement'
      });
      allReplacementTasks.push(...tasks);
      for (const img of missing) {
        if (img.gallery_id && affectedAlbumIds.indexOf(img.gallery_id) === -1) {
          affectedAlbumIds.push(img.gallery_id);
        }
      }
    }
  });

  // Replacements that never reached the provider after all retry attempts.
  const failedReplacements = resolvedReplacementPayloads.filter(
    (img) => !replacementResults.has(img._id) && !img.stopUploading
  );
  yield call(notifyUploadFailureSaga, failedReplacements.length);

  // Phase 2 — compress-only retry for items that reached S3 but compress didn't.
  yield call(retryCompressFailuresSaga, replacementResults, 'replacement');

  // Wait for all forks' compress calls to finish before polling, else the
  // poll exits before BE completes. Wake-aware timeout for zombie forks.
  if (allReplacementTasks.length) {
    yield race({
      joined: join(allReplacementTasks),
      timeout: delay(30_000)
    });
  }

  // Auto-refetch until BE finishes compression so the UI doesn't stick on
  // the spinner.
  for (const albumId of affectedAlbumIds) {
    yield call(pollCompressionUntilSettledSaga, albumId);
  }

  if (affectedAlbumIds.length) {
    yield put(batchUpdateAfterUploadingOfImages());
  }
}

// Fires the 'images-upload-paused' analytics event exactly once per
// pause→resume cycle. Spamming the pause button no longer hits the
// analytics endpoint repeatedly (was triggering 503 rate-limits).
export function* watchPauseAnalytics(): SagaIterator {
  while (true) {
    yield take(pauseUploading);
    const collectionId = yield select(getCollectionId);
    yield put(
      trackEventAction({
        name: ImageUploadAnalyticsEvent.Paused,
        payload: { collection_id: collectionId }
      })
    );
    yield take(restoreUploading);
  }
}

// Shared Phase-1 retry loop for reconcile and replacement.
function* runRetryAttempts(args: RunRetryAttemptsArgs): SagaIterator {
  const computeMissing = () =>
    args.payloads.filter((img) => !args.resultsMap.has(img._id) && !img.stopUploading);

  for (let attempt = 1; attempt <= MAX_RETRY_ATTEMPTS; attempt++) {
    // Breaker tripped — retrying only delays the failure notification.
    if (isProviderCircuitOpen()) {
      break;
    }

    const missing = computeMissing();
    if (!missing.length) {
      break;
    }

    const online: boolean = yield call(waitForOnline, `${args.logLabel} attempt ${attempt}`);
    if (!online) break;

    if (args.beforeAttempt) {
      yield call(args.beforeAttempt, attempt, missing);
    }

    yield call(args.processBatch, missing);

    yield put(batchUpdateAfterUploadingOfImages());
  }
}

// Phase 1 — full re-upload for payloads that never reached S3 PUT.
function* retryMissingUploadsSaga(collectionId: string): SagaIterator {
  yield call(runRetryAttempts, {
    payloads: sessionPayloads,
    resultsMap: sessionResults,
    logLabel: 'reconcile full-upload',
    *beforeAttempt(attempt, missing) {
      const albumIds = Array.from(new Set(missing.map((img) => img.gallery_id).filter(Boolean)));
      yield put(
        trackEventAction({
          name: ImageUploadAnalyticsEvent.RetryAttempt,
          payload: {
            collection_id: collectionId,
            album_ids: albumIds,
            attempt,
            missing_count: missing.length
          }
        })
      );
    },
    *processBatch(missing) {
      const sequential = yield select(getSequentialUploads);
      yield call(runUploadLoop, missing, {
        startTime: performance.now(),
        timestampAlbumId: get(first(missing), 'gallery_id'),
        // Retry's whole point is to redo errored items, so don't skip on error.
        skipOnError: false,
        maxConcurrency: sequential ? 1 : MAX_CONCURRENT_UPLOADS,
        resultsMap: sessionResults,
        checkSession: true,
        routeDuplicates: true,
        logLabel: 'reconcile full-upload',
        // Clear past error state on the image so uploadImageSaga treats it fresh.
        transformPayload: (img) => ({
          ...img,
          status: 'uploading',
          error: undefined,
          errorType: undefined
        })
      });
    }
  });
}

// Phase 2 — compress-only retry. S3 + BE record already exist; we re-call
// compress/processGifUpload against the existing newImage._id (no duplicate
// BE record). Used by both reconcile and replacement.
function* retryCompressFailuresSaga(
  results: ResultsMap,
  logLabel: string = 'reconcile'
): SagaIterator {
  for (let attempt = 1; attempt <= MAX_RETRY_ATTEMPTS; attempt++) {
    const pending: Array<{ id: string; entry: CompressRetryEntry }> = [];
    results.forEach((entry, id) => {
      if (entry !== null) pending.push({ id, entry });
    });
    if (!pending.length) {
      break;
    }

    const online: boolean = yield call(
      waitForOnline,
      `${logLabel} compress-retry attempt ${attempt}`
    );
    if (!online) break;

    const tasks: Task[] = [];

    // Fork each retry through the existing compress semaphore (cap 5) so the
    for (const { id, entry } of pending) {
      if (!isNavigatorOnline()) {
        break;
      }
      const task: Task = yield fork(function* retryCompressOne(): SagaIterator {
        yield call(respectUploadCooldown);
        yield call(acquireCompressSlot);
        try {
          const s3ResponseLike = { newImage: entry.newImage, image: entry.payload };
          const result = entry.isGif
            ? yield call(processGifUpload, s3ResponseLike, entry.payload.file)
            : yield call(compressRequest, s3ResponseLike);
          if (!result?.error && !result?.isOffline) {
            results.set(id, null);
          }
        } finally {
          yield call(releaseCompressSlot);
        }
      });
      tasks.push(task);
    }
    if (tasks.length) yield join(tasks);

    yield put(batchUpdateAfterUploadingOfImages());
  }
}

// Phase 3 — run compression polling once per album that has any
// S3-succeeded image in the session, so the UI eventually hydrates the
// compressed URLs without a manual reload.
function* pollSessionCompressionSaga(): SagaIterator {
  const seen: Record<string, true> = {};
  const albums: string[] = [];
  for (const img of sessionPayloads) {
    if (img.gallery_id && !seen[img.gallery_id] && sessionResults.has(img._id)) {
      seen[img.gallery_id] = true;
      albums.push(img.gallery_id);
    }
  }
  if (!albums.length) return;

  for (const album of albums) {
    yield call(pollCompressionUntilSettledSaga, album);
  }
}

// Coordinator. Reconciles every payload across the current session (all
// batches drained back-to-back). Runs only when the channel buffer is
// empty so multi-batch uploads diff expected vs succeeded as a whole.
function* reconcileUploadSessionSaga(buffer: Buffer<any>): SagaIterator {
  if (!buffer.isEmpty()) {
    return;
  }
  if (sessionPayloads.length === 0) {
    return;
  }

  const collectionId = yield select(getCollectionId);

  try {
    yield call(retryMissingUploadsSaga, collectionId);

    // Still missing = never reached the provider despite all retries.
    const failedUploads = sessionPayloads.filter(
      (img) => !sessionResults.has(img._id) && !img.stopUploading
    );
    yield call(notifyUploadFailureSaga, failedUploads.length);

    // soft-delete the BE records failed uploads left in "uploading" (their
    // bytes count toward storage); offline — park a flag for the drain loop
    if (failedUploads.length) {
      if (isNavigatorOnline()) {
        yield call(softDeleteIncompleteUploadsSaga);
      } else {
        pendingIncompleteCleanup = true;
      }
    }

    yield call(retryCompressFailuresSaga, sessionResults, 'reconcile');
    yield call(pollSessionCompressionSaga);
  } finally {
    resetUploadSession();
    // refresh the storage counter once per finished upload session
    yield put(accessRulesAction());
  }
}

// Cancelled uploads leave BE records stuck in "uploading" — they hold
// manualOrder slots and break later manual sorting. Soft-delete them after a
// fresh fetch (records of just-cancelled forks may not be in the list yet).
const softDeleteIncompleteUploadsSaga = function* (galleryId?: string): SagaIterator {
  // Give in-flight sign-url requests a moment to land on the BE.
  yield delay(1000);
  yield put(fetchImages());
  const { fetched } = yield race({
    fetched: take(fetchImagesSuccess),
    timeout: delay(10_000)
  });
  if (!fetched) return;

  // BE records that reached S3 in this session and whose compress request is
  // still pending — their doc has no compressionStatus yet, so the filter below
  // couldn't tell them apart from a never-uploaded record.
  const s3SucceededIds = new Set<string>();
  sessionResults.forEach((entry) => {
    if (entry?.newImage?._id) s3SucceededIds.add(entry.newImage._id);
  });

  const images: IImage[] = yield select(getImagesList);
  const incompleteIds = images
    .filter(
      (image) =>
        image.status === 'uploading' &&
        !image._id.startsWith('uploading_image_') &&
        // The BE keeps `status: uploading` until compression finishes, so a
        // record with compression activity is NOT an incomplete upload — the
        // file is on S3. Soft-deleting it here raced with the BE compress
        // flipping it back to `uploaded` (orphan with softDeletedAt set) and
        // left the session entry stuck on 'running' (SBK-9428).
        !['running', 'done', 'error'].includes(image.compressionStatus) &&
        !s3SucceededIds.has(image._id) &&
        (!galleryId || image.gallery_id === galleryId)
    )
    .map((image) => image._id);

  if (!incompleteIds.length) return;

  try {
    const collectionId = yield select(getCollectionId);
    yield call(Api.Images.deleteByIDs, {
      collection_id: collectionId,
      image_ids: incompleteIds
    });
    yield put(trimImagesFromItems({ imageIds: incompleteIds }));
  } catch (e) {
    // Best-effort.
  }
};

export function* watchRequest(): SagaIterator {
  const buffer: Buffer<any> = buffers.expanding();
  const requestChannel = yield actionChannel(startUploadingProcess, buffer);

  try {
    while (true) {
      const action = yield take(requestChannel);
      const { stopAll, singleCancel } = yield race({
        task: call(startUploadingProcessSaga, action),
        stopAll: take(stopAllUpload),
        singleCancel: take(stopUploading)
      });

      const collectionId = yield select(getCollectionId);

      if (singleCancel && singleCancel.payload) {
        yield put(galleryUploadingClearAction({ gallery_id: singleCancel.payload }));
        yield call(clearUploadingAboardImageSaga, singleCancel.payload);
        yield put(
          trackEventAction({
            name: ImageUploadAnalyticsEvent.Canceled,
            payload: {
              collection_id: collectionId,
              album_id: get(first(action.payload), 'gallery_id'),
              images_count: action.payload.length
            }
          })
        );

        // Drop the cancelled gallery's images from session payloads so reconcile
        // doesn't try to re-upload them. Succeeded ids can stay — they're keyed
        // by image _id and won't match anything in remaining payloads.
        sessionPayloads = sessionPayloads.filter((img) => img.gallery_id !== singleCancel.payload);

        yield call(softDeleteIncompleteUploadsSaga, singleCancel.payload);
        yield call(refetchImagesSaga);
        yield call(reconcileUploadSessionSaga, buffer);
        // the reconcile above may early-return without the storage refetch —
        // dispatch it explicitly (takeLatest dedupes)
        yield put(accessRulesAction());
        continue;
      }

      if (stopAll !== undefined && stopAll.payload) {
        yield flush(requestChannel);
        yield put(galleriesUploadingClearAction());
        yield put(
          trackEventAction({
            name: ImageUploadAnalyticsEvent.Canceled,
            payload: {
              collection_id: collectionId,
              album_id: get(first(action.payload), 'gallery_id'),
              images_count: action.payload.length
            }
          })
        );

        // Wipe the session — nothing to reconcile after a full cancel.
        resetUploadSession();

        yield call(softDeleteIncompleteUploadsSaga);
        yield call(refetchImagesSaga, 5);
        // the reconcile below early-returns on an empty session — refetch here
        yield put(accessRulesAction());
      }

      yield call(checkAndUploadDuplicateImagesSaga, buffer);
      yield call(reconcileUploadSessionSaga, buffer);
      // triggering batch update after upload process is finished
      yield put(batchUpdateAfterUploadingOfImages());
    }
  } finally {
    if (yield cancelled()) {
      console.log('cancelled');
    }
  }
}

export const uploadImagesSaga = function* ({
  payload
}: ISagaAction<IUploadImagesAction>): SagaIterator {
  // Start uploading
  const { gallery_id, files } = payload;

  const gallery = yield select((state) => getGalleryById(state)(gallery_id));
  const isInUploading = get(gallery, 'uploadingProcess.start', false);
  const uploadSessionImages = yield select(getUploadSessionList);
  const galleryImages = yield select((state) => getGalleryImagesByGalleryID(state)(gallery_id));
  const maxImageOrder = maxBy(galleryImages, (image: IImage) => image.manualOrder) || {
    manualOrder: -1
  };
  let maxOrder = maxImageOrder.manualOrder;
  // uploadOrder must continue past every image that already holds a slot — even
  // ones still in-flight. `galleryImages` is status==='uploaded' only, so a new
  // bunch dropped before the previous one commits would miss them and restart at
  // 0. Take the max over all stored images for the gallery (any status) plus the
  // current upload session. SBK-9036.
  const allGalleryImages: IImage[] = yield select((state) =>
    getImagesByGalleryID(state)(gallery_id)
  );
  const sessionImagesForGallery: IImage[] = uploadSessionImages[gallery_id] || [];
  let maxUploadOrder = [...allGalleryImages, ...sessionImagesForGallery].reduce(
    (max, image) =>
      typeof image.uploadOrder === 'number' && image.uploadOrder > max ? image.uploadOrder : max,
    -1
  );
  // Get Duplicates
  // @ts-ignore
  const duplicatesSelector = yield select(getGalleryImagesDuplicateIDS);

  const uploadSesstionImagesArr = Object.values(uploadSessionImages).flat();
  let duplicates = duplicatesSelector(gallery_id, files, uploadSesstionImagesArr);

  let imagesList: File[] = files;
  if (duplicates.length) {
    yield put(pauseUploading(true));
    // Ask to replace duplicates
    yield put(changeModalState({ key: 'duplicatesImagesModal', state: true }));

    const { payload: duplicatesPayload } = yield take(duplicatesAction);
    // If Skip remove duplicates and remove that images from upload
    if (duplicatesPayload === 'skip') {
      const duplicatesNames = duplicates.map((item: any) => item.name);
      imagesList = files.filter((file) => duplicatesNames.indexOf(file.name) === -1);
      duplicates = [];
    }
    // If User will upload next chunk of images to the same gallery
    if (duplicatesPayload === 'replace' && isInUploading) {
      const galleryImagesOnReplace = yield select((state) =>
        getGalleryImagesByGalleryID(state)(gallery_id)
      );
      const duplicatedIds = duplicates.map((dup: any) => dup.id);
      const imagesWithFilesForReplace = filter(
        galleryImagesOnReplace,
        (image) => galleryImages.file && duplicatedIds.includes(image._id)
      ).map((image) => image.name);
      // failed-upload docs from the server have no local File — don't re-queue them
      const imagesWithFilesNotReplace = filter(
        galleryImagesOnReplace,
        (image) => image.status !== 'uploaded' && !duplicatedIds.includes(image._id) && image.file
      ).map((image) => image.file);
      const newFiles = filter(files, (file) => !imagesWithFilesForReplace.includes(file.name));
      imagesList = [...imagesWithFilesNotReplace, ...newFiles];
      yield put(stopUploading(gallery_id));
      yield all([take(galleryUploadingClearSuccess), take(cleanUpImagesSuccess)]);
    }

    // Hide the existing duplicates immediately so the UI doesn't briefly show
    // both the old and the new image while the server processes the replacement.
    if (duplicatesPayload === 'replace') {
      const duplicateIds = duplicates.map((dup: any) => dup.id).filter(Boolean);
      if (duplicateIds.length) {
        yield put(trimImagesFromItems({ imageIds: duplicateIds }));
      }
    }
  }

  if (!imagesList.length) return;

  // Create preload images to show up in the progress modal
  const tmpImages: IImage[] = imagesList.map((image) => {
    maxOrder++;
    maxUploadOrder++;
    const duplicateId = getDuplicateId(duplicates, image);
    const uploadSizeError =
      image.type === 'image/gif'
        ? getTranslationKey('uploadSizeError.gif', { fileName: image.name })
        : getTranslationKey('uploadSizeError.general', { fileName: image.name });

    return {
      ...DEFAULT_IMAGE,
      uploadedAt: moment().format(),
      _id: uniqueId('uploading_image_'),
      manualOrder: maxOrder,
      uploadOrder: maxUploadOrder,
      compressionStatus: 'none',
      file: image,
      gallery_id,
      mimeType: image.type,
      originalImageName: image.name,
      size: image.size,
      status: 'uploading',
      duplicateId,
      uploadingStatus: 'pre-upload',
      error: !isImageSizeAccepted(image.size, image.type) ? uploadSizeError : '',
      errorType: !isImageSizeAccepted(image.size, image.type) ? 'warning' : ''
    };
  });

  const imagesWithoutErrors = tmpImages.filter(
    (image: IImage) => !(image.error && image.error.length)
  );

  if (!imagesWithoutErrors.length) return;

  const isPaused = yield select(getUploadPauseState);

  if (isPaused) {
    yield put(restoreUploading(true));
    // yield take(imagesChunkUploadingSuccess);
  }

  // Start uploading process to server
  yield put(
    addImagesToUploadSession({
      albumId: get(first(imagesWithoutErrors), 'gallery_id'),
      images: imagesWithoutErrors
    })
  );
  yield put(setModalOpenState({ key: 'progressUploadModal', state: true }));
  yield put(startUploadingProcess(imagesWithoutErrors));
};

const timestamp: any = {};

// Session entries whose BE-side compression we still have to track: the
// compress request answered 'running' (BE timeout), so the entry is already
// 'uploaded' from the user's perspective but its terminal state is unknown.
const isCompressionPending = (img: IImage | null | undefined): boolean =>
  Boolean(img) && (img.status === 'compressing' || img.compressionStatus === 'running');

// Session entries carry the BE `_id` once the compress request has answered,
// so match on it first; the name fallback covers older-shaped entries.
const findLatestForSessionImage = (
  images: IImage[],
  sessionImg: IImage,
  albumId: string
): IImage | undefined => {
  const byId =
    sessionImg._id && !sessionImg._id.startsWith('uploading_image_')
      ? images.find((img) => img?._id === sessionImg._id)
      : undefined;
  if (byId) return byId;

  return images.find(
    (img) =>
      img?.gallery_id === albumId &&
      Boolean(sessionImg.originalImageName) &&
      img?.originalImageName === sessionImg.originalImageName
  );
};

function* pollCompressionUntilSettledSaga(albumId: string): SagaIterator {
  // Polls that returned a fresh list without this session entry in it. The
  // BE record can disappear mid-poll (deleted while compressing), and we must
  // not keep such an entry flagged 'running' forever (SBK-9428).
  const missingPolls: Record<string, number> = {};

  const getPendingSessionImages = function* (): SagaIterator {
    const uploadSessionImages = yield select(getUploadSessionList);
    const sessionAlbumImages: IImage[] = uploadSessionImages?.[albumId] || [];
    return sessionAlbumImages.filter(isCompressionPending);
  };

  const syncUploadSessionWithLatestItems = function* (
    forceFinalize: boolean = false
  ): SagaIterator {
    const pending: IImage[] = yield call(getPendingSessionImages);
    if (!pending.length) return;

    const images: IImage[] = yield select(getImagesList);

    for (const sessionImg of pending) {
      const latest = findLatestForSessionImage(images || [], sessionImg, albumId);

      // The compress request answered 'running' (BE timeout) and the BE-side
      // compression failed afterwards: the doc stays `status: uploading` with
      // `compressionStatus: error`. Surface it like a synchronous compress
      // error instead of a green checkmark (SBK-9428).
      if (latest?.compressionStatus === 'error') {
        yield call(updateImageDataSaga, latest, {
          // @ts-ignore
          error: getTranslationKey('errors.s3Request', {
            originalImageName: sessionImg.originalImageName
          }),
          errorType: 'error'
        });
        yield put(
          updateImageUploadSessionData({
            albumId,
            image: { ...sessionImg, status: 'compressionError', compressionStatus: 'error' }
          })
        );
        continue;
      }

      // Normal path: BE has finished compressing → the entry is fully done.
      if (latest && latest.compressionStatus !== 'running') {
        yield put(
          updateImageUploadSessionData({
            albumId,
            image: {
              ...sessionImg,
              status: 'uploaded',
              compressionStatus: latest.compressionStatus
            }
          })
        );
        continue;
      }

      const missingKey = sessionImg._id || sessionImg.originalImageName || '';
      missingPolls[missingKey] = latest ? 0 : (missingPolls[missingKey] || 0) + 1;
      const goneFromBackend = missingPolls[missingKey] >= COMPRESSION_POLL_MISSING_LIMIT;

      // Poll budget exhausted while the BE still reports 'running', or the BE
      // record is no longer returned at all: we can't track this entry any
      // further. Clear the 'running' flag so the "still optimizing" note and
      // the optimizing icon don't stay on forever; the trailing fetchImages()
      // hydrates the real state whenever the BE actually finishes.
      if (forceFinalize || goneFromBackend) {
        yield put(
          updateImageUploadSessionData({
            albumId,
            image: { ...sessionImg, status: 'uploaded', compressionStatus: 'none' }
          })
        );
      }
    }
  };

  let interval = COMPRESSION_POLL_INTERVAL_MS;
  for (let attempt = 0; attempt < MAX_COMPRESSION_POLL_ATTEMPTS; attempt++) {
    // Always run the first pass: a compress response may still be in flight
    // when polling starts, so the session can't be trusted to be settled yet.
    if (attempt > 0) {
      const pendingBefore: IImage[] = yield call(getPendingSessionImages);
      if (!pendingBefore.length) return;
    }

    yield delay(interval);
    yield put(fetchImages());
    // Wait for the fresh list instead of a fixed delay — on a slow connection
    // a fixed delay leaves us syncing against the stale pre-upload list.
    yield race({
      fetched: take(fetchImagesSuccess),
      timeout: delay(COMPRESSION_POLL_FETCH_TIMEOUT_MS)
    });

    yield call(syncUploadSessionWithLatestItems);

    const pendingAfter: IImage[] = yield call(getPendingSessionImages);
    if (!pendingAfter.length) return;

    interval = Math.min(Math.round(interval * 1.5), COMPRESSION_POLL_MAX_INTERVAL_MS);
  }

  // Poll budget exhausted while the BE still reports 'running' — force-clear
  // the session entries so a slow compression can't hold the modal forever.
  yield call(syncUploadSessionWithLatestItems, true);
}

// Shared fork/drain loop for initial upload, reconcile retry, and replacement.
// Caller picks concurrency, results map, session/duplicate behaviour. Returns
// forked tasks so callers can `join` them for full-pipeline completion.
function* runUploadLoop(
  images: IImage[],
  options: RunUploadLoopOptions
): SagaIterator<RunUploadLoopResult> {
  let inflightForks = 0;
  const slotChannel = yield actionChannel(imageUploadingNext, buffers.expanding());

  function* awaitSlot(): SagaIterator {
    yield take(slotChannel);
    inflightForks--;
    timestamp[options.timestampAlbumId || ''] = (performance.now() - options.startTime) / 1000;
  }

  let forked = 0;
  let skippedMissingDuplicateId = 0;
  const tasks: Task[] = [];

  for (const image of images) {
    if (options.checkSession) {
      const uploadSessionImages = yield select(getUploadSessionList);
      const albumId = image.gallery_id;
      const isStillInSession = (uploadSessionImages?.[albumId] || []).some(
        (img: IImage) => img._id === image._id
      );
      if (!isStillInSession) continue;
    }

    if (image.stopUploading) continue;
    if (options.skipOnError && image.error?.length) continue;

    const hasDuplicateId = Boolean(image.duplicateId && image.duplicateId.length);
    if (options.routeDuplicates) {
      // Initial upload: route duplicates to the replacement queue.
      if (hasDuplicateId) {
        const existsInQueue = newDuplicateImagesQueu.some(
          (q) =>
            q.gallery_id === image.gallery_id && q.originalImageName === image.originalImageName
        );
        if (!existsInQueue) {
          newDuplicateImagesQueu.push(image);
        }
        continue;
      }
    } else if (!hasDuplicateId) {
      // Replacement mode should always have duplicateId; defensive skip.
      if (window?.newrelic?.noticeError) {
        window.newrelic.noticeError('ReplaceMissingDuplicateId', {
          gallery_id: image.gallery_id,
          originalImageName: image.originalImageName
        });
      }
      skippedMissingDuplicateId++;
      continue;
    }

    if (!isNavigatorOnline()) continue;

    while (yield select(getUploadPauseState)) {
      yield take(restoreUploading);
    }

    if (inflightForks >= options.maxConcurrency) {
      yield call(awaitSlot);
    }

    const forkPayload = options.transformPayload ? options.transformPayload(image) : image;
    const task: Task = yield fork(uploadImageSaga, forkPayload, options.resultsMap);
    tasks.push(task);
    forked++;
    inflightForks++;
  }

  let drainTicks = 0;
  while (inflightForks > 0) {
    yield take(slotChannel);
    inflightForks--;
    drainTicks++;
    timestamp[options.timestampAlbumId || ''] = (performance.now() - options.startTime) / 1000;
  }

  return { forked, drainTicks, tasks, skippedMissingDuplicateId };
}

export const startUploadingProcessSaga = function* ({ payload }: ISagaAction<any>): SagaIterator {
  const startTime = performance.now();
  const collectionId = yield select(getCollectionId);
  const batchAlbumId = get(first(payload), 'gallery_id');

  // Push non-duplicates eagerly so an offline-skip inside runUploadLoop
  // still leaves the image visible to reconcile. Duplicates are owned by
  // the replacement saga's own results map.
  const nonDuplicates: IImage[] = payload.filter(
    (img: IImage) => !img.duplicateId || !img.duplicateId.length
  );
  sessionPayloads.push(...nonDuplicates);

  yield put(
    trackEventAction({
      name: ImageUploadAnalyticsEvent.Started,
      payload: {
        collection_id: collectionId,
        album_id: get(first(payload), 'gallery_id'),
        images_count: payload.length
      }
    })
  );

  const sequential = yield select(getSequentialUploads);
  const { drainTicks } = yield call(runUploadLoop, payload, {
    startTime,
    timestampAlbumId: batchAlbumId,
    skipOnError: true,
    maxConcurrency: sequential ? 1 : MAX_CONCURRENT_UPLOADS,
    resultsMap: sessionResults,
    checkSession: true,
    routeDuplicates: true,
    logLabel: 'batch'
  });

  yield put(
    trackEventAction({
      name: ImageUploadAnalyticsEvent.Finished,
      payload: {
        collection_id: collectionId,
        album_id: get(first(payload), 'gallery_id'),
        images_count: payload.length
      }
    })
  );

  yield put(batchUpdateAfterUploadingOfImages());

  // Reconciliation across all batches in this session — and compression
  // polling for every album that produced uploads — runs in watchRequest
  // once the channel buffer is empty (see reconcileUploadSessionSaga).
};

const singUrlRequest = function* (data: IGetSighUrlParams, image: IImage): SagaIterator {
  const response = yield call(multipleAttempts(Api.Images.getSignUrl), data);

  if (response?.error && window?.newrelic?.noticeError) {
    window.newrelic.noticeError('SignUrlRequest', response.error);
  }

  const isOffline = yield call(handleOfflineResponse, response);

  if (isOffline) {
    if (window?.newrelic?.noticeError) {
      window.newrelic.noticeError('SignUrlRequestOffline', response.error);
    }

    return { image, isOffline: true };
  }

  const { storageLimitReached = false, maintenance_mode = false } = response;

  if (response.error) {
    yield call(updateImageDataSaga, image, {
      error: getTranslationKey('errors.signedURLRequest'),
      errorType: 'error'
    });

    return { image, error: response.error };
  }
  if (storageLimitReached) {
    yield put(setModalOpenState({ key: 'spaceLimitReachedModal', state: true }));
    yield put(setModalOpenState({ key: 'progressUploadModal', state: false }));

    yield put(clearImagesUploadSession());
    yield put(stopAllUpload(true));

    return { image, storageLimitReached };
  }
  if (maintenance_mode) {
    yield call(updateImageDataSaga, image, {
      error: getTranslationKey('maintenance.common'),
      errorType: 'warning'
    });

    return { image, maintenance_mode };
  }

  return { ...response, image, newImage: response.newImage };
};

const S3Request = function* (
  singUrlRequestResponse: any,
  abortController?: AbortController
): SagaIterator {
  const { signedUrl, image, newImage, attempt } = singUrlRequestResponse;
  if (!signedUrl) {
    if (window?.newrelic?.noticeError) {
      window.newrelic.noticeError('EmptySignedUrl');
    }

    return { error: 'EmptySignedUrl' };
  }

  if (abortController?.signal?.aborted) {
    return { cancelled: true, image, newImage };
  }

  const response = yield call(
    multipleAttempts(uploadImageToS3, { skipRetryCodes: [403, 500, 502, 503, 504] }),
    signedUrl,
    image.file,
    abortController ? { signal: abortController.signal } : {}
  );

  if (response?.cancelled) {
    return { cancelled: true, image, newImage };
  }

  if (response.error) {
    const isOffline = response.error.message === 'Network Error' && !isNavigatorOnline();

    if (isOffline) {
      return { isOffline };
    }

    if (window?.newrelic?.noticeError) {
      console.log('Send UploadImageToS3 error to newrelic');
      window.newrelic.noticeError('UploadImageToS3', response.error);
    }
  }

  /*
  DOes not make sense because response is always true because its filled by multipleAttempts
  if (!response) {
    yield call(updateImageDataSaga, newImage, {
      // @ts-ignore
      error: getTranslationKey('errors.s3Request', { originalImageName: newImage.originalImageName }),
      errorType: 'error',
      uploadingStatus: 'fail'
    });
  } else { */
  // dont upload a new image right here, do it later
  // yield put(imageUploadingNext());
  return { image, newImage, signedUrl, attempt, error: response.error };
  // }
};

/*
const checkIsImageOnS3Saga = function* (s3Response: any): SagaIterator {
  if (!s3Response) {
    if (window?.newrelic?.noticeError) {
      console.log('Send checkIsImageOnS3Saga error to newrelic');
      window.newrelic.noticeError('checkIsImageOnS3Saga', {
        errorText: 's3Response is empty'
      });
    }
    yield put(imageUploadingNext());
    return;
  }

  const { newImage, image } = s3Response;

  const response = yield call(multipleAttempts(Api.Images.checkS3Exist), {
    image_id: newImage._id,
    original: true
  });

  if (response.error) {
    if (window?.newrelic?.noticeError) {
      console.log('Send CheckS3Exist error to newrelic');
      window.newrelic.noticeError('CheckS3Exist', response.error);
    }

    yield call(updateImageDataSaga, newImage, {
      // @ts-ignore
      error: getTranslationKey('errors.s3Request', { originalImageName: newImage.originalImageName }),
      errorType: 'error',
      uploadingStatus: 'fail'
    });
  } else {
    return { newImage, image };
  }
};
*/
const processGifUpload = function* (s3Response: any, file: File): SagaIterator {
  try {
    const { newImage } = s3Response;
    const dimensionsResponse: IImageDimensions | null = yield call(processImageDimensions, file);
    let dimensions = dimensionsResponse;

    if (!dimensions) {
      dimensions = { width: 0, height: 0 };
    }

    const setGifresponse = yield call(Api.Images.setGifMetaData, newImage._id, dimensions);

    const isOffline = yield call(handleOfflineResponse, setGifresponse);

    if (isOffline) {
      return { isOffline };
    }

    if (setGifresponse.error || !setGifresponse.result.value) {
      yield call(updateImageDataSaga, newImage, {
        // @ts-ignore
        error: getTranslationKey('errors.processGifUpload', {
          originalImageName: newImage.originalImageName
        }),
        errorType: 'error'
      });
      yield put(
        updateImageUploadSessionData({
          albumId: newImage.gallery_id,
          image: { ...newImage, status: 'compressionError' }
        })
      );

      return { error: setGifresponse.error };
    }
    yield delay(500);
    yield call(updateImageDataSaga, setGifresponse.result.value, {
      uploadingStatus: 'uploaded',
      uploadedAt: newImage.uploadedAt
    });
    yield put(
      updateImageUploadSessionData({
        albumId: newImage.gallery_id,
        image: { ...newImage, status: 'uploaded' }
      })
    );

    return { error: null };
  } catch (error) {
    return { error };
  }
};

const compressRequest = function* (s3Response: any): SagaIterator {
  try {
    const { newImage } = s3Response;

    // yield call(updateImageDataSaga, image, { uploadingStatus: 'compressing' });
    const response = yield call(multipleAttempts(Api.Images.compress), {
      image_id: newImage._id
    });

    const offlineAwareMessageKey = navigator?.onLine === false ? 'offlineMessage' : 'generalError';
    const isOffline = yield call(
      handleOfflineResponse,
      response,
      getTranslationKey(offlineAwareMessageKey)
    );

    if (isOffline) {
      return { isOffline };
    }

    if (response.error || response.compressionStatus === 'error') {
      if (window?.newrelic?.noticeError) {
        console.log('Send compressRequest error to newrelic');
        window.newrelic.noticeError('CompressRequest', response.error);
      }
      yield call(updateImageDataSaga, newImage, {
        // @ts-ignore
        error: getTranslationKey('errors.s3Request', {
          originalImageName: newImage.originalImageName
        }),
        errorType: 'error'
      });
      yield put(
        updateImageUploadSessionData({
          albumId: newImage.gallery_id,
          image: { ...newImage, status: 'compressionError' }
        })
      );

      // yield put(imageUploadingNext());
      return { error: response.error };
    }
    yield delay(500);
    yield call(updateImageDataSaga, response, {
      uploadingStatus: 'uploaded',
      uploadedAt: newImage.uploadedAt
    });
    yield put(
      updateImageUploadSessionData({
        albumId: newImage.gallery_id,
        // The file is on S3 and the BE accepted the compress request — that is
        // "uploaded" from the user's perspective (SBK-9215). BE-side compression
        // continues server-side and is tracked separately via `compressionStatus`.
        image: {
          ...newImage,
          status: 'uploaded',
          compressionStatus: response?.compressionStatus
        }
      })
    );

    return { error: null };
  } catch (error) {
    if (window?.newrelic?.noticeError) {
      console.log('Send CompressRequestTryCatch error to newrelic');
      window.newrelic.noticeError('CompressRequestTryCatch', error);
    }
    const { newImage } = s3Response;
    if (newImage) {
      yield call(updateImageDataSaga, newImage, {
        // @ts-ignore
        error: getTranslationKey('errors.s3Request', {
          originalImageName: newImage.originalImageName
        }),
        errorType: 'error'
      });
      yield put(
        updateImageUploadSessionData({
          albumId: newImage.gallery_id,
          image: { ...newImage, status: 'compressionError' }
        })
      );
    }

    return { error };
  }
};

// Soft-deletes the BE record created by sign-url when the provider PUT fails,
// so it doesn't linger as a phantom "uploading" image. Never deletes a
// replacement target (same id as duplicateId).
const softDeleteFailedUploadSaga = function* (payload: IImage, newImageId?: string): SagaIterator {
  if (!newImageId || newImageId === payload.duplicateId) return;

  try {
    const collectionId = yield select(getCollectionId);
    yield call(Api.Images.deleteByIDs, {
      collection_id: collectionId,
      gallery_id: payload.gallery_id,
      image_ids: [newImageId]
    });
  } catch (e) {
    // Best-effort — must not break the upload queue.
  }
};

let compressionFails = 0;

export const uploadImageSaga = function* (payload: IImage, results?: ResultsMap): SagaIterator {
  const collection_id = yield select(getCollectionId);
  const { gallery_id, originalImageName, size, duplicateId, manualOrder, uploadOrder } = payload;
  const logTag = `[upload:${payload._id}|${originalImageName}]`;

  const uploadSessionImages = yield select(getUploadSessionList);
  const isStillInSession = (uploadSessionImages?.[gallery_id] || []).some(
    (img: IImage) => img._id === payload._id
  );
  if (!isStillInSession || payload.stopUploading) {
    yield put(imageUploadingNext());

    return;
  }

  // Breaker tripped — fail fast, no network requests.
  if (isProviderCircuitOpen()) {
    const breakerErrorText = getTranslationKey('errors.s3Request', {
      originalImageName
    });

    yield call(updateImageDataSaga, payload, {
      // @ts-ignore
      error: breakerErrorText,
      errorType: 'error'
    });
    yield put(
      updateImageUploadSessionData({
        albumId: gallery_id,
        image: {
          ...payload,
          status: 'error',
          uploadingStatus: 'fail',
          error: breakerErrorText as string
        }
      })
    );
    yield put(imageUploadingNext());

    return;
  }

  // if (!gallery_id) {
  //   console.error('Missing gallery_id in uploadImageSaga', payload);
  //   yield call(updateImageDataSaga, payload, { error: 'Missing gallery ID', errorType: 'error' });
  //   yield put(imageUploadingNext());

  //   return;
  // }

  const chosenEndpoint: EndpointKey = yield select(getChosenUploadEndpoint);

  const uploadingData: IGetSighUrlParams = {
    collection_id,
    gallery_id,
    originalImageName,
    imageSize: size,
    builderRequest: true,
    endpoint: chosenEndpoint
  };

  // Forward the batch position the client computed in uploadImagesSaga so the
  // backend can preserve selection order regardless of which concurrent upload
  // reaches it first. Only meaningful for new (non-replace) images.
  if (typeof manualOrder === 'number' && Number.isFinite(manualOrder)) {
    uploadingData.manualOrder = manualOrder;
  }

  if (typeof uploadOrder === 'number' && Number.isFinite(uploadOrder)) {
    uploadingData.uploadOrder = uploadOrder;
  }

  if (duplicateId) {
    uploadingData.replace = true;
    uploadingData.existing_image_id = duplicateId || '';
  }

  yield call(updateImageDataSaga, payload, { uploadingStatus: 'uploading' });

  yield put(
    updateImageUploadSessionData({
      albumId: gallery_id,
      image: { ...payload, uploadingStatus: 'uploading' }
    })
  );

  yield call(respectUploadCooldown);
  const singUrlRequestResponse = yield call(singUrlRequest, uploadingData, payload);

  if (singUrlRequestResponse.isOffline) {
    yield put(imageUploadingNext());

    return;
  }

  if (singUrlRequestResponse.error) {
    // Suppress the toast if we went offline between fork and now — reconcile
    // will retry. Otherwise N concurrent forks would emit N toasts.
    if (!isNavigatorOnline()) {
      yield put(imageUploadingNext());

      return;
    }

    const signUrlErrorText = getTranslationKey('errors.s3Request', {
      originalImageName: payload.originalImageName
    });

    yield call(updateImageDataSaga, payload, {
      // @ts-ignore
      error: signUrlErrorText,
      errorType: 'error'
    });
    yield put(
      updateImageUploadSessionData({
        albumId: payload.gallery_id,
        image: {
          ...payload,
          status: 'error',
          uploadingStatus: 'fail',
          error: signUrlErrorText as string
        }
      })
    );
    yield put(imageUploadingNext());

    return;
  }

  if (singUrlRequestResponse.storageLimitReached || singUrlRequestResponse.maintenance_mode) {
    yield call(updateImageDataSaga, payload, {
      // @ts-ignore
      error: getTranslationKey('errors.s3Request', {
        originalImageName: payload.originalImageName
      }),
      errorType: 'error'
    });
    yield put(
      updateImageUploadSessionData({
        albumId: payload.gallery_id,
        image: { ...payload, status: 'error', uploadingStatus: 'fail' }
      })
    );
    yield put(imageUploadingNext());

    return;
  }

  yield call(respectUploadCooldown);

  // Fork (not race) so the timer firing never cancels the upload.
  const slowUploadWatchdog: Task = yield fork(function* (): SagaIterator {
    yield delay(SLOW_UPLOAD_WARNING_MS);
    yield call(notifySlowUploadSaga);
  });

  const s3Response = yield call(S3Request, { ...singUrlRequestResponse, attempt: 0 });

  yield cancel(slowUploadWatchdog);

  if (s3Response.isOffline) {
    yield put(imageUploadingNext());

    return;
  }

  if (s3Response.error) {
    // Network may have dropped between fork time and the S3 PUT response —
    // suppress the toast since the end-of-saga retry will pick this up.
    if (!isNavigatorOnline()) {
      yield put(imageUploadingNext());

      return;
    }

    trackProviderFailure(payload._id);

    const s3ErrorText = getTranslationKey('errors.s3Request', {
      originalImageName: payload.originalImageName
    });

    yield call(updateImageDataSaga, payload, {
      // @ts-ignore
      error: s3ErrorText,
      errorType: 'error'
    });
    yield put(
      updateImageUploadSessionData({
        albumId: payload.gallery_id,
        image: {
          ...payload,
          status: 'error',
          uploadingStatus: 'fail',
          error: s3ErrorText as string
        }
      })
    );
    yield spawn(softDeleteFailedUploadSaga, payload, singUrlRequestResponse?.newImage?._id);
    yield put(imageUploadingNext());

    return;
  }

  // we Currently don't need this. This was a sanity check because wasabi was sometimes not up to date
  // const checkS3response = yield call(checkIsImageOnS3Saga, s3Response);

  // S3 PUT succeeded — clear this image's failure count.
  providerFailuresPerImage.delete(payload._id);

  // S3 PUT done — mark entry (non-null) so reconcile skips re-upload. Set
  // to null below once compress/gif completes.
  const isGif = s3Response?.newImage?.extension?.toLowerCase() === 'gif';
  results?.set(payload._id, { newImage: s3Response.newImage, isGif, payload });

  // Start uploading the next image while compressing the currently uploaded image
  yield put(imageUploadingNext());

  // Compress / gif holds an open BE connection (ImageProcessing service) for
  // its full duration. Cap it with a second semaphore so it doesn't pile up
  // when sign+S3 finishes faster than compress.
  yield call(respectUploadCooldown);
  yield call(acquireCompressSlot);
  try {
    if (isGif) {
      const gifResult = yield call(processGifUpload, s3Response, payload.file);
      if (!gifResult?.error && !gifResult?.isOffline) {
        results?.set(payload._id, null);
      } else {
        console.warn(
          `${logTag} gif-failed err=${!!gifResult?.error} offline=${!!gifResult?.isOffline}`
        );
      }
    } else {
      const compressRequestResult = yield call(compressRequest, s3Response);
      if (!compressRequestResult?.error && !compressRequestResult?.isOffline) {
        results?.set(payload._id, null);
      } else {
        console.warn(
          `${logTag} compress-failed err=${!!compressRequestResult?.error} offline=${!!compressRequestResult?.isOffline}`
        );
      }
      if (compressRequestResult?.error) {
        compressionFails++;
        if (compressionFails >= 5) {
          // prevent to many open compression fails errors
          compressionFails = 0;
          yield put(pauseUploading(true));
        }
      }
    }
  } finally {
    yield call(releaseCompressSlot);
  }
};

const updateImageDataSaga = function* (newImage: IImage, data: Partial<IImage>): SagaIterator {
  const imagesToUpdate = yield select(getImagesToBatchUpdate);
  const galleryImages = yield select(getImagesList);
  let images = [...imagesToUpdate];

  if (!images.length) {
    images = [...galleryImages];
  }

  const isFromDifferentGalleries = (image: IImage) => image.gallery_id !== newImage.gallery_id;
  const isFromSameGalleryWithDifferentNames = (image: IImage) =>
    image.gallery_id === newImage.gallery_id &&
    image.originalImageName !== newImage.originalImageName;

  const imagesUniq = images.filter(
    (image: IImage) => isFromDifferentGalleries(image) || isFromSameGalleryWithDifferentNames(image)
  );

  const duplicateImageIdx = findIndex(imagesUniq, {
    originalImageName: newImage.originalImageName,
    gallery_id: newImage.gallery_id
  });

  if (duplicateImageIdx >= 0 && newImage) {
    imagesUniq.splice(duplicateImageIdx, 1, newImage);
  }
  if (duplicateImageIdx < 0 && newImage) {
    imagesUniq.push({ ...newImage, ...data });
  }

  yield put(pushImagesToBatchUpdateQueue({ itemsToUpdate: imagesUniq }));
};

export const updateSingleImageDataSaga = function* ({ payload }: ISagaAction<any>): SagaIterator {
  const { image, data } = payload;
  yield call(updateImageDataSaga, image, data);
};

const clearUploadingAboardImageSaga = function* (gallery_id: string): SagaIterator {
  const images = yield select(getImagesList);
  const updatedImages = filter(
    images,
    (image: IImage) => !(image.status === 'uploading' && image.gallery_id === gallery_id)
  );
  yield put(cleanUpImagesSuccess({ items: updatedImages }));
};

export const selectSelectionImagesByFilterSaga = function* ({
  payload
}: ISagaAction<ISelectSelectionImageAction>): SagaIterator {
  const contestID = yield select(getCollectionId);
  const images = yield select(getUploadedImagesList);
  const imagesIDs = images.map(({ _id }: IImage) => _id);
  const selections = yield select((state) => getSelectionByCollectionId(state)(contestID));
  const favorites = uniq(
    chain(selections)
      .map((selection) => filter(selection.favorites, { like: true }))
      .flatten()
      .map((favorite) => favorite._image._id)
      .value()
  );

  const isAll = find(payload, { value: 'all' });
  const isLiked = find(payload, { value: 'liked' });
  const notLiked = find(payload, { value: 'notLiked' });
  const isSelectionSelected = Boolean(
    find(payload, (imageFilter) => imageFilter.value.startsWith('selection'))
  );
  const isGallerySelected = Boolean(
    find(payload, (imageFilter) => imageFilter.value.startsWith('gallery'))
  );

  let selected: string[] = [];

  if (isAll) {
    selected = imagesIDs;
  } else if (isLiked) {
    selected = favorites;
  } else if (notLiked) {
    selected = difference(imagesIDs, favorites);
  } else if (isSelectionSelected || isGallerySelected) {
    const selectionsKeyBy = keyBy(selections, '_id');
    const selectionImages = chain(payload)
      .map((imageFilter) => {
        const selectionID = imageFilter.value.replace('selection_', '');
        const selectionFavorites = selectionsKeyBy[selectionID];

        return selectionFavorites ? filter(selectionFavorites.favorites, { like: true }) : [];
      })
      .flatten()
      .map((favorite) => favorite._image._id)
      .value();

    const galleriesImages = chain(payload)
      .map((imageFilter) => {
        const galleryID = imageFilter.value.replace('gallery_', '');

        return images.filter((image: IImage) => image.gallery_id === galleryID);
      })
      .flatten()
      .map((image) => image._id)
      .value();
    selected = uniq([...selectionImages, ...galleriesImages]);
  }

  yield put(selectImageSuccess(selected));
};

export const setGifMetaDataSaga = function* ({
  payload
}: ISagaAction<ISetGifMetaDataAction>): SagaIterator {
  try {
    yield call(Api.Images.setGifMetaData, payload.image_id, payload.dimensions);
  } catch (e) {
    // @ts-ignore
    yield put(errorsGlobalError(e));
  }
};

export const deleteHeaderImageSaga = function* ({
  payload
}: ISagaAction<IDeleteHeaderImageAction>): SagaIterator {
  try {
    const response = yield call(Api.Images.deleteHeaderImage, payload.imageId, {
      collectionId: payload.collectionId,
      galleryPresetId: payload.galleryPresetId
    });

    ApiErrors.checkOnApiError(response);

    if (payload.galleryPresetId) {
      yield put(fetchPresetsAction());
    }

    if (payload.collectionId) {
      const images = yield select(getImagesList);
      const updated = images.filter((image: IImage) => image._id !== payload.imageId);
      yield put(fetchImagesSuccess({ items: updated }));
    }

    yield put(showNotifySuccess({}));
  } catch (e) {
    // @ts-ignore
    yield put(errorsGlobalError(e));
  }
};
